import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  #packEventTarget;

  connectedCallback() {
    super.connectedCallback();
    if (!this.closest('[data-tmfs-pack-control]')) return;

    this.#packEventTarget = this.closest('.shopify-section, dialog, product-card');
    this.#packEventTarget?.addEventListener(ThemeEvents.variantUpdate, this.#onPackVariantUpdate);
    this.addEventListener('input', this.#updatePackSummary);
    this.#updatePackSummary();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updatePackSummary();
  }

  disconnectedCallback() {
    this.#packEventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#onPackVariantUpdate);
    this.removeEventListener('input', this.#updatePackSummary);
    this.#packEventTarget = undefined;
    super.disconnectedCallback();
  }

  #onPackVariantUpdate = (event) => {
    const control = this.closest('[data-tmfs-pack-control]');
    if (!control || event.detail.data.productId !== control.dataset.productId || !event.detail.resource) return;

    const nextControl = event.detail.data.html.getElementById(control.id);
    if (!(nextControl instanceof HTMLElement)) return;

    control.dataset.testsPerPack = nextControl.dataset.testsPerPack;
    this.#updatePackSummary();
  };

  #updatePackSummary = () => {
    const control = this.closest('[data-tmfs-pack-control]');
    if (!control) return;

    const label = control.querySelector('[data-pack-label-text]');
    const summary = control.querySelector('[data-pack-summary]');
    const { quantityInput } = this.refs;
    if (!label || !summary || !quantityInput) return;

    const testsPerPack = Number(control.dataset.testsPerPack) || 1;
    const multipleTests = testsPerPack > 1;
    label.hidden = !multipleTests;
    summary.hidden = !multipleTests;
    quantityInput.setAttribute('aria-label', multipleTests ? control.dataset.packLabel : control.dataset.singleLabel);
    if (multipleTests) {
      quantityInput.setAttribute('aria-describedby', summary.id);
    } else {
      quantityInput.removeAttribute('aria-describedby');
    }

    const packs = Number(quantityInput.value);
    if (!Number.isInteger(packs) || packs < 1) {
      summary.textContent = '';
      return;
    }
    summary.textContent = (packs === 1 ? control.dataset.summaryOne : control.dataset.summaryMany)
      .replaceAll('__PACKS__', String(packs))
      .replaceAll('__TESTS__', String(packs * testsPerPack));
  };

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    this.#updatePackSummary();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
